<template>
  <div class="dashboard">
    <div class="dashboard-header">
      <div>
        <h1 class="dashboard-header__title">Dashboard</h1>
        <p class="dashboard-header__subtitle">Network overview and top-performing suppliers.</p>
      </div>
      <button type="button" class="dashboard-refresh" :disabled="loading" @click="fetchStats">
        <i class="fa fa-rotate" :class="{ 'fa-spin': loading }"></i> Refresh
      </button>
    </div>

    <div v-if="loading && !loaded" class="dashboard-status">Loading dashboard…</div>
    <div v-else-if="loadError" class="dashboard-status dashboard-status--error">{{ loadError }}</div>

    <template v-else>
      <!-- ============ KPI cards ============ -->
      <div class="kpi-grid">
        <div v-for="kpi in kpis" :key="kpi.label" class="kpi-card">
          <div class="kpi-card__icon" :style="{ background: kpi.tint, color: kpi.color }">
            <i :class="kpi.icon"></i>
          </div>
          <div class="kpi-card__body">
            <span class="kpi-card__value">{{ kpi.value }}</span>
            <span class="kpi-card__label">{{ kpi.label }}</span>
            <span v-if="kpi.sub" class="kpi-card__sub">{{ kpi.sub }}</span>
          </div>
        </div>
      </div>

      <div class="dashboard-row">
        <!-- ============ Members by type ============ -->
        <div class="panel panel--members">
          <div class="panel__head">
            <h3 class="panel__title">Members by Type</h3>
            <p class="panel__desc">Total registrations per business type.</p>
          </div>

          <div class="type-bars">
            <div v-for="t in memberTypeBars" :key="t.key" class="type-bar">
              <div class="type-bar__row">
                <span class="type-bar__label">{{ t.label }}</span>
                <span class="type-bar__value">{{ t.count }}</span>
              </div>
              <div class="type-bar__track">
                <div class="type-bar__fill" :style="{ width: t.percent + '%', background: t.color }"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- ============ Approval breakdown ============ -->
        <div class="panel panel--approval">
          <div class="panel__head">
            <h3 class="panel__title">Approval Breakdown</h3>
            <p class="panel__desc">All suppliers, by overall approval status.</p>
          </div>

          <div class="donut-wrap">
            <div class="donut" :style="{ background: donutGradient }">
              <div class="donut__hole">
                <strong>{{ totalMembers }}</strong>
                <span>Total</span>
              </div>
            </div>
            <ul class="donut-legend">
              <li v-for="seg in approvalSegments" :key="seg.key">
                <span class="donut-legend__dot" :style="{ background: seg.color }"></span>
                <span class="donut-legend__label">{{ seg.label }}</span>
                <span class="donut-legend__value">{{ seg.count }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="dashboard-row">
        <!-- ============ Top by connections ============ -->
        <div class="panel panel--top">
          <div class="panel__head">
            <h3 class="panel__title">Top Suppliers by Connections</h3>
            <p class="panel__desc">Most "My Supplier" connections, approved only.</p>
          </div>

          <ol v-if="topByConnections.length" class="top-list">
            <li v-for="(s, i) in topByConnections" :key="s.id" class="top-list__item">
              <span class="top-list__rank">{{ i + 1 }}</span>
              <span class="top-list__avatar">
                <img v-if="s.photo" :src="getFileUrl(s.photo)" :alt="s.company_name" />
                <span v-else>{{ initials(s.company_name) }}</span>
              </span>
              <span class="top-list__name">{{ s.company_name }}</span>
              <span class="top-list__metric">{{ s.connections_count }} <small>connections</small></span>
            </li>
          </ol>
          <p v-else class="panel__empty">No approved connections yet.</p>
        </div>

        <!-- ============ Top by reviews ============ -->
        <div class="panel panel--top">
          <div class="panel__head">
            <h3 class="panel__title">Top Rated Suppliers</h3>
            <p class="panel__desc">Highest average rating, most reviews as tiebreaker.</p>
          </div>

          <ol v-if="topByReviews.length" class="top-list">
            <li v-for="(s, i) in topByReviews" :key="s.id" class="top-list__item">
              <span class="top-list__rank">{{ i + 1 }}</span>
              <span class="top-list__avatar">
                <img v-if="s.photo" :src="getFileUrl(s.photo)" :alt="s.company_name" />
                <span v-else>{{ initials(s.company_name) }}</span>
              </span>
              <span class="top-list__name">{{ s.company_name }}</span>
              <span class="top-list__stars">
                <span v-for="n in 5" :key="n" class="top-list__star" :class="{ 'is-filled': n <= Math.round(s.avg_rating) }">★</span>
                <small>({{ s.reviews_count }})</small>
              </span>
            </li>
          </ol>
          <p v-else class="panel__empty">No reviews yet.</p>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'

export default {
  name: 'AdminDashboard',

  data() {
    return {
      loading: false,
      loaded: false,
      loadError: '',
      stats: null,
    }
  },

  computed: {
    totalMembers() {
      return this.stats?.total_members ?? 0
    },

    kpis() {
      if (!this.stats) return []
      return [
        {
          label: 'Total Members',
          value: this.stats.total_members ?? 0,
          icon: 'fa fa-users',
          tint: 'rgba(11, 23, 153, 0.1)',
          color: 'var(--color-primary)',
        },
        {
          label: 'Materials Registered',
          value: this.stats.total_materials ?? 0,
          icon: 'fa fa-box',
          tint: 'rgba(69, 123, 157, 0.12)',
          color: '#2d5773',
        },
        {
          label: 'Products Registered',
          value: this.stats.total_products ?? 0,
          icon: 'fa fa-tags',
          tint: 'rgba(209, 73, 91, 0.1)',
          color: '#a5293d',
        },
        {
          label: 'New Signups',
          value: this.stats.recent_signups_30d ?? 0,
          sub: 'last 30 days',
          icon: 'fa fa-user-plus',
          tint: 'rgba(236, 153, 58, 0.14)',
          color: '#b5670f',
        },
      ]
    },

    memberTypeBars() {
      if (!this.stats) return []
      const byType = this.stats.members_by_type || {}
      const rows = [
        { key: 'supplier', label: 'Suppliers', color: 'var(--color-primary)' },
        { key: 'manufacturer', label: 'Manufacturers', color: 'var(--color-tint-warm)' },
        { key: 'service_provider', label: 'Service Providers', color: 'var(--color-secondary)' },
      ]
      const max = Math.max(1, ...rows.map((r) => byType[r.key] || 0))
      return rows.map((r) => {
        const count = byType[r.key] || 0
        return { ...r, count, percent: Math.round((count / max) * 100) }
      })
    },

    approvalSegments() {
      if (!this.stats) return []
      const breakdown = this.stats.approval_breakdown || {}
      const colorMap = {
        approved: '#1a7f4b',
        pending: '#ec993a',
        in_progress: '#6c757d',
        rejected: '#b3261e',
      }
      const labelMap = {
        approved: 'Approved',
        pending: 'Pending',
        in_progress: 'In Progress',
        rejected: 'Rejected',
      }
      return Object.entries(breakdown).map(([key, count], i) => ({
        key,
        count,
        label: labelMap[key] || this.titleCase(key),
        color: colorMap[key] || ['#457b9d', '#6b5ce7', '#e07a5f'][i % 3],
      }))
    },

    donutGradient() {
      const total = this.approvalSegments.reduce((sum, s) => sum + s.count, 0)
      if (!total) return 'var(--color-border)'
      let cursor = 0
      const stops = this.approvalSegments.map((seg) => {
        const start = (cursor / total) * 360
        cursor += seg.count
        const end = (cursor / total) * 360
        return `${seg.color} ${start}deg ${end}deg`
      })
      return `conic-gradient(${stops.join(', ')})`
    },

    topByConnections() {
      return this.stats?.top_by_connections || []
    },
    topByReviews() {
      return this.stats?.top_by_reviews || []
    },
  },

  mounted() {
    this.fetchStats()
  },

  methods: {
    getFileUrl(filePath) {
      if (!filePath) return ''
      if (filePath.startsWith('http')) return filePath
      const baseUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000'
      return `${baseUrl}/${filePath}`
    },
    initials(name) {
      return (name || '')
        .split(' ')
        .map((w) => w[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    },
    titleCase(str) {
      if (!str) return ''
      return str.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    },

   async fetchStats() {
  this.loading = true
  this.loadError = ''
  const result = await form_api_submit({
    url: 'dashboard/admin-stats',
    method: 'GET',
    show_success_toast: false,
    show_error_toast: true,
  })
  if (result.status) {
    this.stats = result.data
    this.loaded = true
  } else {
    this.loadError = result.message || 'Could not load dashboard stats. Please try again.'
  }

  this.loading = false
}
  },
}
</script>

<style scoped>
.dashboard {
  padding: 4px 4px 24px;
}
.dashboard * {
  box-sizing: border-box;
}

.dashboard-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 22px;
}
.dashboard-header__title {
  margin: 0 0 4px;
  font-size: 22px;
  font-weight: 800;
  color: var(--color-text-dark);
}
.dashboard-header__subtitle {
  margin: 0;
  font-size: 13px;
  color: var(--color-text-muted);
}
.dashboard-refresh {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 16px;
  border-radius: 10px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-dark);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.dashboard-refresh:hover:not(:disabled) {
  background: var(--color-tint-light);
  border-color: var(--color-secondary);
}
.dashboard-refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.dashboard-status {
  padding: 60px 20px;
  text-align: center;
  color: var(--color-text-muted);
  font-size: 13.5px;
}
.dashboard-status--error {
  color: #b3261e;
}

/* ==========================================================================
   KPI cards
   ========================================================================== */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 14px;
  margin-bottom: 20px;
}

.kpi-card {
  display: flex;
  align-items: center;
  gap: 14px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  padding: 18px;
  box-shadow: 0 4px 16px -12px rgba(12, 18, 83, 0.16);
}

.kpi-card__icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  font-size: 17px;
}

.kpi-card__body {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}
.kpi-card__value {
  font-size: 22px;
  font-weight: 800;
  color: var(--color-text-dark);
  line-height: 1.1;
}
.kpi-card__label {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-muted);
}
.kpi-card__sub {
  font-size: 11px;
  color: var(--color-text-muted);
  opacity: 0.85;
}

/* ==========================================================================
   Panels (two-up rows)
   ========================================================================== */
.dashboard-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.panel {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 18px;
  padding: 22px;
  box-shadow: 0 4px 16px -12px rgba(12, 18, 83, 0.14);
}

.panel__head {
  margin-bottom: 18px;
}
.panel__title {
  margin: 0 0 2px;
  font-size: 14.5px;
  font-weight: 800;
  color: var(--color-text-dark);
}
.panel__desc {
  margin: 0;
  font-size: 12px;
  color: var(--color-text-muted);
}
.panel__empty {
  margin: 0;
  font-size: 12.5px;
  color: var(--color-text-muted);
}

/* ---------- Members by type ---------- */
.type-bars {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.type-bar__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.type-bar__label {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-dark);
}
.type-bar__value {
  font-size: 13px;
  font-weight: 700;
  color: var(--color-text-dark);
}
.type-bar__track {
  height: 8px;
  border-radius: 999px;
  background: var(--color-tint-light);
  overflow: hidden;
}
.type-bar__fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.4s ease;
}

/* ---------- Approval donut ---------- */
.donut-wrap {
  display: flex;
  align-items: center;
  gap: 28px;
  flex-wrap: wrap;
}
.donut {
  width: 140px;
  height: 140px;
  border-radius: 50%;
  flex-shrink: 0;
  display: grid;
  place-items: center;
}
.donut__hole {
  width: 92px;
  height: 92px;
  border-radius: 50%;
  background: var(--color-surface);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.donut__hole strong {
  font-size: 20px;
  font-weight: 800;
  color: var(--color-text-dark);
  line-height: 1;
}
.donut__hole span {
  font-size: 10.5px;
  color: var(--color-text-muted);
  font-weight: 600;
  margin-top: 2px;
}

.donut-legend {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-width: 140px;
}
.donut-legend li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}
.donut-legend__dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
}
.donut-legend__label {
  flex: 1;
  color: var(--color-text-dark);
  font-weight: 600;
}
.donut-legend__value {
  color: var(--color-text-muted);
  font-weight: 700;
}

/* ---------- Top lists ---------- */
.top-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.top-list__item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 6px;
  border-bottom: 1px solid var(--color-border);
}
.top-list__item:last-child {
  border-bottom: none;
}
.top-list__rank {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 7px;
  background: var(--color-tint-light);
  color: var(--color-text-muted);
  font-size: 11px;
  font-weight: 700;
  display: grid;
  place-items: center;
}
.top-list__avatar {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  overflow: hidden;
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  color: #fff;
  display: grid;
  place-items: center;
  font-size: 11.5px;
  font-weight: 700;
}
.top-list__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.top-list__name {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-dark);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.top-list__metric {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 700;
  color: var(--color-primary);
}
.top-list__metric small {
  font-weight: 500;
  color: var(--color-text-muted);
  margin-left: 2px;
}
.top-list__stars {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 2px;
}
.top-list__star {
  color: var(--color-border);
  font-size: 13px;
}
.top-list__star.is-filled {
  color: #ec993a;
}
.top-list__stars small {
  margin-left: 4px;
  color: var(--color-text-muted);
  font-weight: 600;
}

/* ==========================================================================
   Responsive
   ========================================================================== */
@media (max-width: 900px) {
  .dashboard-row {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 480px) {
  .dashboard-header {
    flex-direction: column;
    align-items: stretch;
  }
  .donut-wrap {
    justify-content: center;
  }
}
</style>